<template>
  <portal-layout>
    <div v-loading="loading" class="judge-detail-page">
      <div class="page-head">
        <h2 class="page-title">提交详情</h2>
        <el-button size="small" @click="goBack">返回</el-button>
      </div>

      <template v-if="judge">
        <!-- 判题概况 -->
        <div class="card">
          <div class="judge-summary">
            <span :class="['result-status', statusClass(judge.status)]">
              {{ judge.statusName || statusText(judge.status) }}
            </span>
            <span class="summary-item">提交ID：{{ judge.submitId }}</span>
            <span class="summary-item">题目：{{ judge.title || judge.displayPid }}</span>
            <span class="summary-item">语言：{{ judge.language }}</span>
            <span v-if="isOi" class="summary-item summary-score">得分：{{ judge.score || 0 }} / 100</span>
            <span class="summary-item">耗时：{{ judge.time }} ms</span>
            <span class="summary-item">内存：{{ judge.memory }} KB</span>
            <span class="summary-item">代码长度：{{ judge.length }} B</span>
            <span class="summary-item">提交时间：{{ judge.submitTime }}</span>
          </div>
          <pre v-if="judge.errorMessage" class="error-message">{{ judge.errorMessage }}</pre>
        </div>

        <!-- 测试点明细：按题目 open_case_result 开关控制 -->
        <div v-if="judge.openCaseResult" class="card">
          <h3 class="section-title">测试点明细</h3>
          <el-table :data="cases" stripe size="small" style="width: 100%">
            <el-table-column prop="seq" label="测试点" width="90"></el-table-column>
            <el-table-column label="结果" width="100">
              <template slot-scope="scope">
                <span :class="['status-text', statusClass(scope.row.status)]">
                  {{ scope.row.statusName || statusText(scope.row.status) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="耗时" width="100">
              <template slot-scope="scope">{{ scope.row.time }} ms</template>
            </el-table-column>
            <el-table-column label="内存" width="110">
              <template slot-scope="scope">{{ scope.row.memory }} KB</template>
            </el-table-column>
            <el-table-column v-if="isOi" prop="score" label="得分" width="90"></el-table-column>
            <el-table-column label="输出信息" min-width="240">
              <template slot-scope="scope">
                <span class="output-cell">{{ scope.row.userOutput }}</span>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="cases.length === 0" class="empty-tip">暂无测试点明细</div>
        </div>

        <!-- 提交代码 -->
        <div class="card">
          <h3 class="section-title">提交代码</h3>
          <pre class="code-block">{{ judge.code || '（代码未公开）' }}</pre>
        </div>
      </template>
    </div>
  </portal-layout>
</template>
<script>
import PortalLayout from '@/components/PortalLayout.vue';
import api from '@/common/api';
import storage from '@/common/storage.js';

const STATUS_TEXT = {
  '-10': '未提交', '-5': '未知', '-4': '已取消', '-3': '格式错误', '-2': '编译错误',
  '-1': '答案错误', '0': '通过', '1': '超出时间限制', '2': '超出内存限制', '3': '运行时错误',
  '4': '系统错误', '5': '等待中', '6': '编译中', '7': '判题中', '8': '部分通过', '9': '提交中', '10': '提交失败'
};

export default {
  name: 'PortalJudgeDetail',
  components: { PortalLayout },
  data() {
    return {
      loading: false,
      judge: null,
      cases: []
    }
  },
  computed: {
    // OI 题（type=1）才展示得分；ACM 题只判通过/未通过
    isOi() {
      return Number(this.judge && this.judge.type) === 1;
    }
  },
  created() {
    if (!storage.getString('portalMember')) {
      this.$router.push({ name: 'PortalLogin', query: { redirect: this.$route.fullPath } }).catch(() => {});
      return;
    }
    this.submitId = this.$route.query.id;
    if (!this.submitId) {
      this.$message.error('缺少提交参数');
      return;
    }
    this.queryDetail();
  },
  methods: {
    queryDetail() {
      this.loading = true;
      api.hoj_problem_judge_detail({ submitId: Number(this.submitId) }).then((r) => {
        if (r.data.code === "200" && r.data.data) {
          this.judge = r.data.data.judge;
          this.cases = r.data.data.cases || [];
        } else {
          this.$message.error(r.data.msg || '记录加载失败');
        }
      }).catch(() => {}).finally(() => {
        this.loading = false;
      });
    },
    goBack() {
      this.$router.back();
    },
    statusText(status) {
      return STATUS_TEXT[String(status)] || '未知';
    },
    statusClass(status) {
      const s = String(status);
      if (s === '0') return 'status-ac';
      if (s === '-1' || s === '-2' || s === '-3' || s === '1' || s === '2' || s === '3' || s === '4') return 'status-error';
      return 'status-other';
    }
  }
};
</script>
<style scoped>
.card {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  color: #303133;
  margin: 0;
}
.judge-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
  margin-bottom: 12px;
}
.result-status {
  font-size: 18px;
  font-weight: 600;
  margin-right: 8px;
}
.summary-item {
  font-size: 13px;
  color: #606266;
}
.summary-score {
  color: #E6A23C;
  font-weight: 600;
}
.section-title {
  font-size: 16px;
  color: #303133;
  margin: 0 0 12px 0;
  padding-left: 10px;
  border-left: 3px solid #409EFF;
  line-height: 1.2;
}
.status-ac {
  color: #67C23A;
}
.status-error {
  color: #F56C6C;
}
.status-other {
  color: #E6A23C;
}
.error-message {
  background-color: #fef0f0;
  border-radius: 4px;
  padding: 12px;
  margin: 0;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 260px;
  overflow-y: auto;
  color: #F56C6C;
}
.code-block {
  background-color: #f6f8fa;
  border-radius: 6px;
  padding: 14px 16px;
  margin: 0;
  font-size: 13px;
  font-family: Consolas, Monaco, monospace;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 520px;
  overflow-y: auto;
  color: #24292e;
}
.output-cell {
  font-size: 12px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-all;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 30px 0;
  font-size: 14px;
}
</style>
